import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import CheckPayments from './CheckPayments';
import ReconProcessingSmartPayables from './ReconProcessingSmartPayables';
import PositivePayFiles from './PositivePayFiles';
import loader from '../../assets/img/loader.svg';

const SmartPayablesManagement = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [smartPayablesAccounts, setSmartPayablesAccounts] = useState([]);
  const [
    selectedSmartPayablesAccount,
    setSelectedSmartPayablesAccount,
  ] = useState('');
  const [loaded, setLoaded] = useState(true);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);

    const currentParams = new URLSearchParams(searchParams);
    currentParams.set('tab', newValue.toString());
    navigate(`/admin/checkManagement/management?${currentParams.toString()}`, {
      replace: true,
    });
  };

  const fetchSmartPayablesAccounts = async () => {
    try {
      setLoaded(true);
      const response = await axios.get(API_BASE_URL + '/smartpayables');
      setSmartPayablesAccounts(response.data || []);
    } catch (error) {
      console.error('Error fetching SmartPayables accounts:', error);
    } finally {
      setLoaded(false);
    }
  };

  useEffect(() => {
    fetchSmartPayablesAccounts();
  }, []);

  useEffect(() => {
    const accountId = searchParams.get('id');
    const tabParam = searchParams.get('tab');

    if (accountId && smartPayablesAccounts.length > 0) {
      setSelectedSmartPayablesAccount(accountId);
    }

    if (tabParam !== null) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 2) {
        setActiveTab(tabIndex);
      }
    }
  }, [searchParams, smartPayablesAccounts]);

  const handleSmartPayablesAccountChange = (event) => {
    const accountId = event.target.value;
    setSelectedSmartPayablesAccount(accountId);

    const currentParams = new URLSearchParams(searchParams);

    if (accountId) {
      currentParams.set('id', accountId);
    } else {
      currentParams.delete('id');
    }

    navigate(`/admin/checkManagement/management?${currentParams.toString()}`, {
      replace: true,
    });
  };

  const handleBackClick = () => {
    const organizationId = searchParams.get('organizationId');
    const url = organizationId
      ? `/admin/updateOrganization/${organizationId}#check-management`
      : '/admin/checkManagement';

    navigate(url);
  };

  return (
    <Card>
      <CardHeader
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        color='primary'>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={handleBackClick}
            sx={{
              color: 'white !important',
              padding: '4px',
              '& .MuiSvgIcon-root': {
                color: 'white !important',
              },
            }}
            size='small'>
            <ArrowBack sx={{ color: 'white !important' }} />
          </IconButton>
          <h4 style={{ margin: 0 }}>
            {t('smartPayablesManagementPage.title')}
          </h4>
        </Box>
      </CardHeader>
      <CardBody>
        <div style={{ marginBottom: '20px' }}>
          <div className='custom-select' style={{ maxWidth: '400px' }}>
            <FormControl variant='standard' fullWidth>
              <InputLabel id='smartpayables-account-select-label'>
                {t('smartPayablesManagementPage.selectAccount')}
              </InputLabel>
              <Select
                labelId='smartpayables-account-select-label'
                value={selectedSmartPayablesAccount}
                onChange={handleSmartPayablesAccountChange}
                className='full-width-elem'>
                <MenuItem value=''>
                  <em>{t('smartPayablesManagementPage.selectAnAccount')}</em>
                </MenuItem>
                {smartPayablesAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.bank_alias} - {account.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>

        {loaded && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <img src={loader} alt={t('common.loading')} />
          </div>
        )}

        {!loaded && (
          <>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab
                label={t('smartPayablesManagementPage.tabs.checkPayments')}
              />
              <Tab
                label={t('smartPayablesManagementPage.tabs.positivePayFiles')}
              />
              <Tab
                label={t('smartPayablesManagementPage.tabs.reconProcessing')}
              />
            </Tabs>

            {activeTab === 0 && selectedSmartPayablesAccount && (
              <CheckPayments
                key={selectedSmartPayablesAccount}
                role='admin'
                smartPayablesAccountId={selectedSmartPayablesAccount}
              />
            )}

            {activeTab === 1 && selectedSmartPayablesAccount && (
              <PositivePayFiles
                key={selectedSmartPayablesAccount}
                smartPayablesAccountId={selectedSmartPayablesAccount}
              />
            )}

            {activeTab === 2 && selectedSmartPayablesAccount && (
              <ReconProcessingSmartPayables
                key={selectedSmartPayablesAccount}
                smartPayablesAccountId={selectedSmartPayablesAccount}
              />
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default SmartPayablesManagement;
